import api from './api'

/**
 * API client da feature de Backup (FEA721).
 *
 * Endpoints expostos pelo BackupController:
 *  - /api/tenants/{tenantId}/backup-policies (lista/cria)
 *  - /api/backup-policies/{id} (get/put/delete)
 *  - /api/managed-devices/{managedDeviceId}/backup-paths (lista/cria)
 *  - /api/backup-paths/{id} (get/put/delete)
 *  - /api/backup-paths/{id}/policies/{policyId} (attach/detach)
 *  - /api/backup-paths/{id}/run?policyId=... (run-now)
 *  - /api/managed-devices/{managedDeviceId}/backup-executions (histórico)
 *  - /api/backup-executions/{id}/download (presigned URL)
 *  - /api/backup-executions/{id}/restore
 */
export const backupApi = {
  // Policies (escopo: tenant)
  listPolicies: async (tenantId) => {
    const r = await api.get(`/tenants/${tenantId}/backup-policies`)
    return r.data
  },
  createPolicy: async (tenantId, data) => {
    const r = await api.post(`/tenants/${tenantId}/backup-policies`, data)
    return r.data
  },
  getPolicy: async (id) => {
    const r = await api.get(`/backup-policies/${id}`)
    return r.data
  },
  updatePolicy: async (id, data) => {
    const r = await api.put(`/backup-policies/${id}`, data)
    return r.data
  },
  deletePolicy: async (id) => {
    await api.delete(`/backup-policies/${id}`)
  },

  // Paths (escopo: managed device)
  listPaths: async (managedDeviceId) => {
    const r = await api.get(`/managed-devices/${managedDeviceId}/backup-paths`)
    return r.data
  },
  createPath: async (managedDeviceId, data) => {
    const r = await api.post(`/managed-devices/${managedDeviceId}/backup-paths`, data)
    return r.data
  },
  updatePath: async (id, data) => {
    const r = await api.put(`/backup-paths/${id}`, data)
    return r.data
  },
  deletePath: async (id) => {
    await api.delete(`/backup-paths/${id}`)
  },
  attachPolicy: async (pathId, policyId) => {
    await api.post(`/backup-paths/${pathId}/policies/${policyId}`)
  },
  detachPolicy: async (pathId, policyId) => {
    await api.delete(`/backup-paths/${pathId}/policies/${policyId}`)
  },
  runNow: async (pathId, policyId) => {
    const r = await api.post(`/backup-paths/${pathId}/run?policyId=${policyId}`)
    return r.data
  },

  // Executions
  listExecutions: async (managedDeviceId, limit = 200) => {
    const r = await api.get(`/managed-devices/${managedDeviceId}/backup-executions?limit=${limit}`)
    return r.data
  },
  getDownloadUrl: async (executionId) => {
    const r = await api.get(`/backup-executions/${executionId}/download`)
    return r.data
  },
  restore: async (executionId, targetDatabase) => {
    const r = await api.post(`/backup-executions/${executionId}/restore`, { targetDatabase })
    return r.data
  },

  // Visão geral do tenant (último backup/falha/retidos por device)
  overview: async (tenantId) => {
    const r = await api.get(`/tenants/${tenantId}/backup-overview`)
    return r.data
  },
}

// ── Helpers ────────────────────────────────────────────────────────────────

export const DOW_LABELS = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom']
export const DOW_BITS = [1, 2, 4, 8, 16, 32, 64]

export const dowMaskToFlags = (mask) => DOW_BITS.map((bit) => (mask & bit) === bit)
export const flagsToDowMask = (flags) =>
  flags.reduce((acc, on, idx) => (on ? acc | DOW_BITS[idx] : acc), 0)

export const BUCKET_FLAGS = {
  Daily: 1,
  Weekly: 2,
  Monthly: 4,
  Yearly: 8,
}

// A API serializa enums como string ("Daily, Monthly"); aceita número por compatibilidade.
export const bucketNames = (b) => {
  if (b == null) return []
  if (typeof b === 'string') return b.split(',').map((x) => x.trim()).filter((x) => x && x !== 'None')
  return Object.entries(BUCKET_FLAGS).filter(([, bit]) => (b & bit) === bit).map(([name]) => name)
}

const BUCKET_LABELS = { Daily: 'Diário', Weekly: 'Semanal', Monthly: 'Mensal', Yearly: 'Anual' }
export const formatBuckets = (b) => bucketNames(b).map((n) => BUCKET_LABELS[n] || n).join(' · ') || '—'

// Status: "Running" | "Success" | "Failed" (ou 0/1/2 legado)
export const normalizeStatus = (s) => (typeof s === 'number' ? ['Running', 'Success', 'Failed'][s] : s)

export const SOURCE_KIND_LABELS = {
  File: 'Arquivo (Linux)',
  RouterOsExport: 'MikroTik: export + backup',
}

export const TIMEZONES = [
  'America/Sao_Paulo',
  'America/Manaus',
  'America/Belem',
  'America/Fortaleza',
  'America/Cuiaba',
  'America/Rio_Branco',
  'America/Noronha',
  'UTC',
]

export const summarizeRetention = (p) => {
  const parts = []
  if (p.retainDaily > 0) parts.push(`${p.retainDaily} dias`)
  if (p.retainWeekly > 0) parts.push(`${p.retainWeekly} semanas`)
  if (p.retainMonthly > 0) parts.push(`${p.retainMonthly} meses`)
  parts.push(p.retainYearly > 0 ? `${p.retainYearly} anos` : 'anual ilimitado')
  return parts.join(' · ')
}

export const formatBytes = (n) => {
  if (n == null) return '—'
  let v = n
  for (const u of ['B', 'KB', 'MB', 'GB', 'TB']) {
    if (v < 1024) return `${u === 'B' ? v.toFixed(0) : v.toFixed(1)} ${u}`
    v /= 1024
  }
  return `${v.toFixed(1)} PB`
}
